import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { NextSeo } from 'next-seo'
import { useDispatch, useSelector } from 'react-redux'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { wrapper } from '../store/configStore'
import { SERVER_IP } from '../store/middleware/api'
import { getFilteredPackages, packagesSetFilters, getPackageFilters, loadGeoPackages } from '../store/slices/entities/packages'
import { getWorkingOrder } from '../store/slices/entities/orders'
import { getList, loadEntityForce } from '../store/slices/entities/crmActions'
import { getMedia, getUser } from '../store/slices/user'
import { mainContentSetCSS, toggleLeftMenu } from '../store/slices/ui'
import SinglePack from '../components/SinglePack'
import Button from '../components/Button'
import PackageFilters from '../components/PackageFilters'
import PublicPackageAdvisor from '../components/design-system/commerce/PublicPackageAdvisor'
import PageShell from '../components/design-system/foundations/PageShell'
import styles from '../components/design-system/commerce/PublicPackageAdvisorPage.module.scss'

export const getStaticProps = wrapper.getStaticProps(async () => {
  try {
    const response = await axios.get(`${SERVER_IP}/inventory/pack/public`)
    return { props: { publicPackages: response.data || [] }, revalidate: 900 }
  } catch (error) {
    return { props: { publicPackages: [] }, revalidate: 60 }
  }
})

const OrderPackageDirectory = () => {
  const dispatch = useDispatch()
  const router = useRouter()
  const order = useSelector(getWorkingOrder)
  const { tablet } = useSelector(getMedia)
  const { geo } = useSelector(getUser)
  const allPackages = useSelector(getList('packages')) || []
  const filters = useSelector(getPackageFilters)
  const filteredPackages = useSelector(getFilteredPackages) || []
  const [prices, setPrices] = useState()
  const [rec, setRec] = useState()
  const [type, setType] = useState({ wedding: true, private: true, corporate: true, celebration: true, trade: true, concert: true, dj: true, band: true, musician: true, mics: true, visual: true })

  useEffect(() => {
    dispatch(loadEntityForce('packages'))
    dispatch(packagesSetFilters({ wedding: true, private: true, corporate: true, celebration: true, trade: true, concert: true, dj: true, band: true, musician: true, mics: true, visual: true, max: 1000000, min: 0 }))
  }, [dispatch])

  useEffect(() => {
    if (geo?.lat && geo?.lng) dispatch(loadGeoPackages(geo, true))
  }, [dispatch, geo])

  useEffect(() => {
    dispatch(mainContentSetCSS(tablet ? '' : 'left'))
    return () => dispatch(mainContentSetCSS(''))
  }, [dispatch, tablet])

  useEffect(() => {
    if (!router.query.filter) return
    if (router.query.filter === 'av') dispatch(packagesSetFilters({ wedding: false, private: false, corporate: true, celebration: false, trade: true, concert: false }))
    else dispatch(packagesSetFilters({ wedding: false, private: false, corporate: false, celebration: false, trade: false, concert: false, [router.query.filter]: true }))
  }, [dispatch, router.query.filter])

  const packages = [...filteredPackages].sort((a, b) => Number(b.price || 0) - Number(a.price || 0))

  return <div className='packagePage'>
    <div className='mainBody'>
      <PackageFilters packages={packages} filters={filters} allPackages={allPackages} rec={rec} setRec={setRec} prices={prices} setPrices={setPrices} type={type} setType={setType} />
      <div className='mainBody-scroll'>
        <div className='headRow'>
          <strong>{packages.length} setups available for this order</strong>
          <Button className='btn-3 highLight' onClick={() => router.push(`/planner?id=${order._id}`)}>Back to Package Builder</Button>
          {tablet ? <Button className='btn-3 highLight' onClick={() => dispatch(toggleLeftMenu())}>Filters</Button> : null}
        </div>
        {packages.map(pack => <SinglePack key={pack._id} pack={pack} />)}
      </div>
    </div>
  </div>
}

const Packages = ({ publicPackages = [] }) => {
  const order = useSelector(getWorkingOrder)

  if (order?._id) return <OrderPackageDirectory />

  return <PageShell className={styles.page}>
    <NextSeo
      title='Recommended AV Equipment Setups | Miami Sound Rental'
      description='Compare customizable AV equipment starting setups for corporate events, celebrations, weddings, concerts, presentations, and trade shows. Adjust every setup before booking.'
      canonical='https://miamisoundrental.com/packages'
      openGraph={{
        type: 'website',
        url: 'https://miamisoundrental.com/packages',
        title: 'Recommended AV Equipment Setups | Miami Sound Rental',
        description: 'Compare customizable AV equipment starting setups, see equipment pricing, and adjust every setup in the Event Builder.',
        images: [{
          url: 'https://dnw-server.s3.us-east-2.amazonaws.com/msr_og.jpg',
          width: 1200,
          height: 630,
          alt: 'Miami Sound Rental recommended AV equipment setups',
        }],
        site_name: 'Miami Sound Rental',
      }}
    />

    <header className={styles.hero}>
      <div>
        <span>Editable AV starting points</span>
        <h1>Recommended AV setups for real events</h1>
        <p>Choose a sensible starting configuration, see the equipment price, and change anything before you book. For complex productions, an AV expert can design the complete system with you.</p>
        <div className={styles.heroActions}>
          <a href='#setup-advisor'>Find my starting setup</a>
          <a href='tel:+13059744411'>Talk to an AV expert</a>
        </div>
      </div>
    </header>

    {publicPackages.length ? <PublicPackageAdvisor packages={publicPackages} /> : <section className={styles.unavailable}>
      <h2>Let an AV expert design your setup</h2>
      <p>The public recommendations are being reviewed for current equipment and accurate fit guidance. We will not show a setup that has not passed those checks.</p>
      <Link href='/contact-us'>Request an AV recommendation</Link>
    </section>}
  </PageShell>
}

export default Packages
